import * as React from "react"
import { BiCalendarEvent } from "react-icons/bi"
import { FaApple, FaGoogle, FaMapMarkerAlt } from "react-icons/fa"
import { IoIosShareAlt } from "react-icons/io"
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Table,
  Text,
  Th,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import "dayjs/locale/nl"

import { CompetitionDeadlinesFragment, CompetitionDetailFragment } from "lib/graphql"
import { disabilityProvisionSrc } from "lib/helpers/disabilityProvisionSrc"
import { DEADLINE_CATEGORIES } from "lib/static/deadlines"
import { disabilityProvisionsMultiOptions } from "lib/static/options"

import { WEB_URL } from "../lib/config"
import { CompetitionActions } from "./CompetitionActions"
import { Modal } from "./Modal"
import { NoData } from "./NoData"

dayjs.extend(utc)
dayjs.locale("nl")

export const CompetitionInfo: React.FC<{ competition: CompetitionDetailFragment }> = ({ competition }) => {
  const { firstRoundDate, time, content, name, location } = competition
  const modalProps = useDisclosure()
  let calendarLink: string | undefined
  let calendarUrl: string | undefined

  const whatsappMessageText = `Ik vind dit een tof event, volgens mij is het ook iets voor jou! ${WEB_URL}/wedstrijd/${competition.slug}`

  if (competition && firstRoundDate) {
    const competitionTime = time?.split("-")
    const start = competitionTime ? competitionTime[0]?.replace(/\s/g, "") : undefined
    const end = competitionTime ? competitionTime[1]?.replace(/\s/g, "") : undefined
    const [, startHours, startMinutes] = start?.match(/(\d{2}):(\d{2})/) || []
    const [, endHours, endMinutes] = end?.match(/(\d{2}):(\d{2})/) || []

    const startTime =
      endHours && startHours
        ? dayjs.utc(firstRoundDate).hour(parseInt(startHours)).minute(parseInt(startMinutes))
        : dayjs.utc(firstRoundDate).hour(0).minute(0)
    const endTime =
      endHours && startHours
        ? dayjs.utc(firstRoundDate).hour(parseInt(endHours)).minute(parseInt(endMinutes))
        : dayjs.utc(firstRoundDate)

    const startDate =
      startTime && endTime
        ? startTime.toISOString().replace(/-|:|\.\d+/g, "")
        : dayjs.utc(firstRoundDate).format("YYYYMMDD") || ""
    const endDate =
      startTime && endTime
        ? endTime.toISOString().replace(/-|:|\.\d+/g, "")
        : dayjs.utc(firstRoundDate).add(1, "day").format("YYYYMMDD") || ""

    calendarLink = encodeURI(
      [
        "https://www.google.com/calendar/render",
        "?action=TEMPLATE",
        "&text=" + (name || ""),
        "&dates=" + startDate,
        "/" + endDate,
        "&details=" + (content || ""),
        "&location=" + (location || ""),
        "&sprop=&sprop=name:",
      ].join(""),
    )

    calendarUrl = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      "DTSTART:" + startDate,
      "DTEND:" + endDate,
      "LOCATION:" + location,
      "SUMMARY:" + name,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n")
  }

  const handleAddEventApple = (e?: React.SyntheticEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (!calendarUrl) return

    const filename = "download.ics"
    const blob = new Blob([calendarUrl], { type: "text/calendar;charset=utf-8" })
    const link = document.createElement("a")
    link.href = window.URL.createObjectURL(blob)
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleAddEventGoogle = (e?: React.SyntheticEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (!calendarLink) return
    window.open(calendarLink, "_blank", "noopener,noreferrer")
  }

  const deadlines = {
    deadlineDJ: competition?.deadlineDJ,
    deadlineDans: competition?.deadlineDans,
    deadlineExpo: competition?.deadlineExpo,
    deadlineFashion: competition?.deadlineFashion,
    deadlineFilm: competition?.deadlineFilm,
    deadlineMuziek: competition?.deadlineMuziek,
    deadlineTaal: competition?.deadlineTaal,
    deadlineTheater: competition?.deadlineTheater,
  } as CompetitionDeadlinesFragment

  Object.keys(deadlines).forEach((key) => {
    const castKey = key as keyof CompetitionDeadlinesFragment
    if (deadlines[castKey] === null || deadlines[castKey] === undefined) {
      delete deadlines[castKey]
    }
  })

  return (
    <Stack spacing={1}>
      <Flex justify="flex-end">
        <Link href={`https://api.whatsapp.com/send?text=${whatsappMessageText}`} isExternal>
          <Button leftIcon={<IoIosShareAlt />} variant="ghost">
            Delen
          </Button>
        </Link>
      </Flex>
      <Stack bg="gray.700" borderRadius="xl" py={8} px={8} spacing={8} position="relative">
        <Flex alignItems="flex-start">
          <Box as={BiCalendarEvent} mt={1} mr={6} boxSize="24px" color="gray.300" />
          <Stack spacing={1}>
            {firstRoundDate ? (
              <Text fontSize="lg" fontWeight="bold">
                {dayjs.utc(firstRoundDate).locale("nl").format("dddd DD MMM")}
              </Text>
            ) : (
              <Text fontSize="lg" fontWeight="bold">
                Datum volgt
              </Text>
            )}
            <Text fontSize="lg" fontWeight="bold">
              {time}
            </Text>
            <Menu>
              {firstRoundDate && (
                <>
                  <MenuButton
                    fontWeight="bold"
                    color="yellow.500"
                    aria-label="Toevoegen aan kalender"
                    fontSize="lg"
                    _hover={{ textDecor: "underline" }}
                    _focus={{
                      boxShadow: "0 0 0 3px var(--chakra-colors-blue-400)",
                      outline: "none",
                    }}
                  >
                    <Text color="yellow.500">Toevoegen aan agenda</Text>
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={handleAddEventApple}
                      as="div"
                      cursor="pointer"
                    >
                      <Flex
                        flexDirection="row"
                        alignItems="center"
                      >
                        <FaApple size={20} />
                        <Text fontSize="md" marginLeft="10px">
                          Apple Calendar
                        </Text>
                      </Flex>
                    </MenuItem>
                    <MenuItem
                      onClick={handleAddEventGoogle}
                      as="div"
                      cursor="pointer"
                    >
                      <Flex
                        flexDirection="row"
                        alignItems="center"
                      >
                        <FaGoogle />
                        <Text fontSize="md" marginLeft="10px">
                          Google Calendar
                        </Text>
                      </Flex>
                    </MenuItem>
                  </MenuList>
                </>
              )}
            </Menu>
          </Stack>
        </Flex>{" "}
        {location && (
          <Flex alignItems="flex-start">
            <Box as={FaMapMarkerAlt} mt={1} mr={6} boxSize="24px" color="gray.300" />

            <Stack>
              <Text fontSize="lg" fontWeight="bold">
                {location}
              </Text>
              {!location.includes("Online") && !location.includes("TBA") && !location.includes("Zoom") && (
                <Link
                  fontSize="lg"
                  variant="ghost"
                  color="yellow.500"
                  target="_blank"
                  rel="noopener nofollower"
                  href={`//maps.google.com/?q=${location}`}
                  fontWeight="bold"
                  aria-label="Ga naar kaart (wordt geopend in een nieuw tabblad)"
                >
                  Routebeschrijving
                </Link>
              )}
            </Stack>
          </Flex>
        )}
        <HStack maxW="100%" flexWrap="wrap">
          {competition.disabilityProvisions.length > 0 &&
            competition.disabilityProvisions.map((provision, i) => {
              return (
                <Tooltip
                  key={i}
                  label={disabilityProvisionsMultiOptions[provision]}
                  bg="yellow.500"
                  color="black"
                  fontWeight="bold"
                  aria-label={disabilityProvisionsMultiOptions[provision]}
                >
                  <Image
                    w="40px"
                    src={disabilityProvisionSrc(disabilityProvisionsMultiOptions[provision])}
                    alt={disabilityProvisionsMultiOptions[provision]}
                    aria-label={disabilityProvisionsMultiOptions[provision]}
                  tabIndex={0}  
                  />
                </Tooltip>
              )
            })}
        </HStack>
        <Stack spacing={2}>
          <CompetitionActions competition={competition} />
          {Object.keys(deadlines).length !== 0 && (
            <Button variant="ghost" color="yellow.500" onClick={modalProps.onOpen}>
              Deadlines bekijken
            </Button>
          )}
        </Stack>
      </Stack>
      <Modal {...modalProps}>
        <CompetitionDeadlines deadlines={deadlines} />
      </Modal>
    </Stack>
  )
}

interface CompetitionDeadlinesProps {
  deadlines: CompetitionDeadlinesFragment
}

function CompetitionDeadlines(props: CompetitionDeadlinesProps) {
  const deadlines = props.deadlines

  // group deadlines by date
  const deadlinesByDate = Object.keys(deadlines).reduce((acc, key) => {
    const castKey = key as keyof CompetitionDeadlinesFragment

    const date = dayjs.utc(deadlines[castKey]).format("YYYY-MM-DD")
    if (!acc[date]) {
      acc[date] = []
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    acc[date].push(castKey)
    return acc
  }, {} as { [date: string]: [keyof CompetitionDeadlinesFragment] | [] })

  if (Object.keys(deadlines).length === 0)
    return (
      <Center h="300px">
        <NoData>Geen Deadlines</NoData>
      </Center>
    )

  return (
    <Table size="sm">
      {/* Render deadlines grouped by date */}
      {Object.keys(deadlinesByDate)
        .sort((a, b) => dayjs.utc(a).valueOf() - dayjs.utc(b).valueOf())
        .map((date, i) => {
          return (
            <Stack key={i} py={8}>
              <Tr>
                <Th colSpan={2} p={0}>
                  <Text fontSize="lg" pb={2} color="yellow.500">
                    {dayjs.utc(date).format("DD/MM/YYYY")}
                  </Text>
                </Th>
              </Tr>
              {deadlinesByDate[date].map((deadline, j) => {
                return (
                  <Tr key={j} w="100%">
                    <Th p={0}>
                      <Text fontSize="lg" color="white">
                        {DEADLINE_CATEGORIES[deadline]}
                      </Text>
                    </Th>
                  </Tr>
                )
              })}
            </Stack>
          )
        })}
    </Table>
  )
}
